import { defineStore } from 'pinia'
import axios from '../config/api.js'

export const useChatStore = defineStore('chat', {
  state: () => ({
    // État des conversations
    conversations: [],
    currentConversation: null,
    messages: [],
    
    // État de l'IA
    availableModels: {
      local: [
        { id: 'llama2', name: 'Llama 2', provider: 'ollama', status: 'available' },
        { id: 'codellama', name: 'Code Llama', provider: 'ollama', status: 'available' },
        { id: 'mistral', name: 'Mistral', provider: 'ollama', status: 'downloading' }
      ],
      remote: [
        { id: 'gpt-4', name: 'GPT-4', provider: 'openai', status: 'available' },
        { id: 'gpt-3.5-turbo', name: 'GPT-3.5 Turbo', provider: 'openai', status: 'available' }
      ]
    },
    currentModel: 'llama2',
    modelStatus: 'connected',
    
    // Configuration
    config: {
      temperature: 0.7,
      maxTokens: 2000,
      systemPrompt: 'Tu es un assistant IA spécialisé dans l\'automatisation du traitement des emails commerciaux. Tu aides les utilisateurs avec l\'analyse des emails, l\'extraction de commandes, la détection d\'anomalies et la gestion des données clients.',
      contextWindow: 4000,
      streamResponse: true
    },
    
    // État de l'interface
    isLoading: false,
    isConnected: true,
    error: null,
    
    // Statistiques
    stats: {
      totalMessages: 0,
      totalTokensUsed: 0,
      averageResponseTime: 0,
      successRate: 0.95,
      modelUsage: {
        'llama2': 45,
        'gpt-4': 30,
        'gpt-3.5-turbo': 25
      }
    },
    
    // Contexte métier
    businessContext: {
      emails: {
        total: 1247,
        unprocessed: 23,
        withOrders: 156,
        anomalies: 8
      },
      orders: {
        total: 89,
        pending: 12,
        validated: 67,
        rejected: 10
      },
      customers: {
        total: 234,
        active: 189,
        new: 12
      },
      products: {
        total: 567,
        outOfStock: 23,
        lowStock: 45
      }
    },
    
    // Prompts et templates
    promptTemplates: [
      {
        id: 'email_analysis',
        title: 'Analyse d\'emails',
        category: 'emails',
        prompt: 'Analyse les emails non traités et identifie les commandes potentielles. Pour chaque email, indique : le statut (commande/information/spam), les produits mentionnés, les quantités, et le niveau de confiance de l\'extraction.',
        variables: ['timeframe', 'account']
      },
      {
        id: 'anomaly_detection',
        title: 'Détection d\'anomalies',
        category: 'orders',
        prompt: 'Examine les commandes récentes et détecte les anomalies potentielles : prix inhabituels, quantités suspectes, clients nouveaux avec grosses commandes, produits discontinués. Classe par niveau de risque.',
        variables: ['period', 'threshold']
      },
      {
        id: 'performance_report',
        title: 'Rapport de performance',
        category: 'analytics',
        prompt: 'Génère un rapport de performance pour la période spécifiée incluant : taux de traitement des emails, précision de l\'extraction, temps de réponse moyen, et recommandations d\'amélioration.',
        variables: ['startDate', 'endDate']
      },
      {
        id: 'customer_insights',
        title: 'Insights clients',
        category: 'customers',
        prompt: 'Analyse le comportement des clients et identifie : les tendances d\'achat, les clients à risque de churn, les opportunités d\'upselling, et les segments de clientèle émergents.',
        variables: ['segment', 'metric']
      }
    ],
    
    // Historique des évaluations
    evaluations: [],
    
    // Cache des réponses
    responseCache: new Map()
  }),
  
  getters: {
    // Modèles disponibles
    localModels: (state) => state.availableModels.local,
    remoteModels: (state) => state.availableModels.remote,
    allModels: (state) => [...state.availableModels.local, ...state.availableModels.remote],
    
    // Modèle actuel
    currentModelInfo: (state) => {
      return state.allModels.find(model => model.id === state.currentModel)
    },
    
    // Conversations
    recentConversations: (state) => {
      return state.conversations
        .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))
        .slice(0, 10)
    },
    
    // Messages de la conversation actuelle
    currentMessages: (state) => {
      return state.currentConversation ? state.currentConversation.messages : []
    },
    
    // Statistiques calculées
    totalConversations: (state) => state.conversations.length,
    
    averageMessagesPerConversation: (state) => {
      if (state.conversations.length === 0) return 0
      const totalMessages = state.conversations.reduce((sum, conv) => sum + conv.messages.length, 0)
      return Math.round(totalMessages / state.conversations.length)
    },
    
    // Modèles par statut
    availableModels: (state) => {
      return state.allModels.filter(model => model.status === 'available')
    },
    
    downloadingModels: (state) => {
      return state.allModels.filter(model => model.status === 'downloading')
    },
    
    // Contexte formaté
    formattedBusinessContext: (state) => {
      const { emails, orders, customers, products } = state.businessContext
      return [
        `Emails: ${emails.total} total, ${emails.unprocessed} non traités, ${emails.withOrders} avec commandes`,
        `Commandes: ${orders.total} total, ${orders.pending} en attente, ${orders.validated} validées`,
        `Clients: ${customers.total} total, ${customers.active} actifs, ${customers.new} nouveaux`,
        `Produits: ${products.total} total, ${products.outOfStock} en rupture, ${products.lowStock} stock faible`
      ].join('\n')
    },
    
    // Templates par catégorie
    templatesByCategory: (state) => {
      const categories = {}
      state.promptTemplates.forEach(template => {
        if (!categories[template.category]) {
          categories[template.category] = []
        }
        categories[template.category].push(template)
      })
      return categories
    }
  },
  
  actions: {
    // Gestion des modèles
    async switchModel(modelId) {
      try {
        this.isLoading = true
        this.error = null
        
        // Vérifier la disponibilité du modèle
        const model = this.allModels.find(m => m.id === modelId)
        if (!model) {
          throw new Error(`Modèle ${modelId} non trouvé`)
        }
        
        if (model.status !== 'available') {
          throw new Error(`Modèle ${model.name} non disponible (${model.status})`)
        }
        
        // Simuler la connexion au modèle
        await new Promise(resolve => setTimeout(resolve, 1000))
        
        this.currentModel = modelId
        this.modelStatus = 'connected'
        this.isConnected = true
        
        // Mettre à jour les statistiques
        this.stats.modelUsage[modelId] = (this.stats.modelUsage[modelId] || 0) + 1
        
        return { success: true, model: model.name }
        
      } catch (error) {
        this.error = error.message
        this.isConnected = false
        throw error
      } finally {
        this.isLoading = false
      }
    },
    
    async checkModelStatus(modelId) {
      try {
        // Simuler la vérification du statut
        const model = this.allModels.find(m => m.id === modelId)
        if (!model) return { status: 'not_found' }
        
        // Simuler différents statuts
        const statuses = ['available', 'downloading', 'error', 'offline']
        const randomStatus = Math.random() > 0.1 ? 'available' : statuses[Math.floor(Math.random() * statuses.length)]
        
        model.status = randomStatus
        return { status: randomStatus, model: model.name }
        
      } catch (error) {
        console.error('Erreur lors de la vérification du modèle:', error)
        return { status: 'error', error: error.message }
      }
    },
    
    // Gestion des conversations
    async createConversation(title = null) {
      const conversation = {
        id: Date.now().toString(),
        title: title || `Conversation ${new Date().toLocaleDateString()}`,
        messages: [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        model: this.currentModel,
        config: { ...this.config }
      }
      
      this.conversations.unshift(conversation)
      this.currentConversation = conversation
      
      return conversation
    },
    
    async loadConversation(conversationId) {
      try {
        const conversation = this.conversations.find(c => c.id === conversationId)
        if (!conversation) {
          throw new Error('Conversation non trouvée')
        }
        
        this.currentConversation = conversation
        return conversation
        
      } catch (error) {
        this.error = error.message
        throw error
      }
    },
    
    async deleteConversation(conversationId) {
      try {
        const index = this.conversations.findIndex(c => c.id === conversationId)
        if (index === -1) {
          throw new Error('Conversation non trouvée')
        }
        
        this.conversations.splice(index, 1)
        
        if (this.currentConversation?.id === conversationId) {
          this.currentConversation = null
        }
        
        return { success: true }
        
      } catch (error) {
        this.error = error.message
        throw error
      }
    },
    
    // Envoi de messages
    async sendMessage({ message, context = '', attachments = [], conversationId = null }) {
      try {
        this.isLoading = true
        this.error = null
        
        // Créer une conversation si nécessaire
        if (!this.currentConversation) {
          await this.createConversation()
        }
        
        const conversation = this.currentConversation
        
        // Ajouter le message utilisateur
        const userMessage = {
          id: Date.now().toString(),
          role: 'user',
          content: message,
          timestamp: new Date().toISOString(),
          attachments
        }
        
        conversation.messages.push(userMessage)
        
        // Préparer le contexte complet
        const fullContext = this.buildFullContext(context)
        
        // Vérifier le cache
        const cacheKey = this.generateCacheKey(message, fullContext, this.currentModel)
        if (this.responseCache.has(cacheKey)) {
          const cachedResponse = this.responseCache.get(cacheKey)
          conversation.messages.push({
            ...cachedResponse,
            id: (Date.now() + 1).toString(),
            timestamp: new Date().toISOString(),
            cached: true
          })
          
          conversation.updatedAt = new Date().toISOString()
          return cachedResponse
        }
        
        // Appel API réel vers le backend
        const startTime = Date.now()
        const apiResponse = await axios.post('/api/chat', { prompt: message })
        const responseTime = Date.now() - startTime
        
        const payload = apiResponse?.data || {}
        const content = payload.response_email || payload.response || JSON.stringify(payload)
        const tokens = Math.ceil((content || '').length / 4) // estimation simple
        
        // Ajouter la réponse de l'assistant
        const assistantMessage = {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          content: content,
          timestamp: new Date().toISOString(),
          model: this.currentModel,
          tokens: tokens,
          responseTime,
          confidence: payload.confidence || undefined
        }
        
        conversation.messages.push(assistantMessage)
        conversation.updatedAt = new Date().toISOString()
        
        // Mettre en cache la réponse
        this.responseCache.set(cacheKey, assistantMessage)
        
        // Mettre à jour les statistiques
        this.updateStats(responseTime, tokens, true)
        
        return assistantMessage
        
      } catch (error) {
        console.error('Erreur lors de l\'envoi du message:', error)
        this.error = error?.response?.status ? `Erreur réseau: ${error.response.status}` : (error.message || 'Erreur réseau')
        
        // Ajouter un message d'erreur
        if (this.currentConversation) {
          this.currentConversation.messages.push({
            id: (Date.now() + 1).toString(),
            role: 'assistant',
            content: 'Désolé, une erreur s\'est produite lors du traitement de votre message. Veuillez réessayer.',
            timestamp: new Date().toISOString(),
            isError: true
          })
        }
        
        this.updateStats(0, 0, false)
        throw error
        
      } finally {
        this.isLoading = false
      }
    },
    
    // Appel au modèle IA (simulé)
    async callAiModel({ message, context, history, model, config }) {
      // Simuler un délai de réponse réaliste
      const delay = Math.random() * 2000 + 1000 // 1-3 secondes
      await new Promise(resolve => setTimeout(resolve, delay))
      
      // Simuler différents types de réponses selon le contenu
      let response = ''
      let tokens = 0
      let confidence = 0.85
      
      const lowerMessage = message.toLowerCase()
      
      if (lowerMessage.includes('email') || lowerMessage.includes('mail')) {
        response = this.generateEmailAnalysisResponse(message, context)
        tokens = Math.floor(Math.random() * 300) + 200
        confidence = 0.92
      } else if (lowerMessage.includes('commande') || lowerMessage.includes('order')) {
        response = this.generateOrderAnalysisResponse(message, context)
        tokens = Math.floor(Math.random() * 250) + 150
        confidence = 0.88
      } else if (lowerMessage.includes('anomalie') || lowerMessage.includes('erreur')) {
        response = this.generateAnomalyResponse(message, context)
        tokens = Math.floor(Math.random() * 400) + 250
        confidence = 0.90
      } else if (lowerMessage.includes('rapport') || lowerMessage.includes('statistique')) {
        response = this.generateReportResponse(message, context)
        tokens = Math.floor(Math.random() * 500) + 300
        confidence = 0.85
      } else if (lowerMessage.includes('client') || lowerMessage.includes('customer')) {
        response = this.generateCustomerResponse(message, context)
        tokens = Math.floor(Math.random() * 200) + 100
        confidence = 0.87
      } else {
        response = this.generateGenericResponse(message, context)
        tokens = Math.floor(Math.random() * 150) + 100
        confidence = 0.80
      }
      
      return {
        content: response,
        tokens,
        confidence,
        model
      }
    },
    
    // Générateurs de réponses spécialisées
    generateEmailAnalysisResponse(message, context) {
      const responses = [
        `## Analyse des emails récents\n\nJ'ai analysé les **23 emails non traités** dans votre système. Voici un résumé :\n\n### 📧 Emails avec commandes détectées (8)\n- **Email de ACME Corp** : Commande de 150 unités du produit REF-001\n- **Email de TechSolutions** : Demande de devis pour 50 licences logicielles\n- **Email de GlobalTrade** : Commande urgente de 200 pièces REF-045\n\n### ⚠️ Emails nécessitant attention (3)\n- Format inhabituel détecté\n- Informations manquantes (prix, quantités)\n- Nouveau client sans historique\n\n### ✅ Recommandations\n1. Valider manuellement les 3 emails suspects\n2. Configurer des règles pour GlobalTrade (client récurrent)\n3. Mettre à jour les templates d'extraction\n\n**Taux de confiance moyen** : 87%`,
        
        `## État du traitement des emails\n\n### 📊 Statistiques actuelles\n- **Total emails** : 1,247\n- **Non traités** : 23 (1.8%)\n- **Avec commandes** : 156 (12.5%)\n- **Anomalies détectées** : 8 (0.6%)\n\n### 🔍 Analyse détaillée\n\n**Emails prioritaires** :\n1. **Commande urgente** - Client Premium (délai 24h)\n2. **Nouveau client** - Validation manuelle requise\n3. **Montant élevé** - Vérification automatique déclenchée\n\n**Patterns détectés** :\n- Augmentation des commandes le mardi (+15%)\n- Nouveaux formats de commande de 3 clients\n- Amélioration du taux d'extraction (+5% cette semaine)\n\n### 🎯 Actions recommandées\n- Traiter les 8 emails prioritaires\n- Mettre à jour les règles d'extraction\n- Planifier une revue des templates`
      ]
      
      return responses[Math.floor(Math.random() * responses.length)]
    },
    
    generateOrderAnalysisResponse(message, context) {
      const responses = [
        `## Analyse des commandes\n\n### 📋 État actuel\n- **Commandes totales** : 89\n- **En attente de validation** : 12\n- **Validées** : 67\n- **Rejetées** : 10\n\n### 🔍 Commandes nécessitant attention\n\n**Commande #CMD-2024-0156** \n- Client : TechCorp Solutions\n- Montant : 15,750 €\n- Statut : ⚠️ Anomalie détectée (prix inhabituel)\n\n**Commande #CMD-2024-0157**\n- Client : Nouveau client\n- Montant : 8,200 €\n- Statut : 🔍 Validation manuelle requise\n\n### 📈 Tendances\n- Volume +12% vs semaine précédente\n- Panier moyen : 2,340 €\n- Taux de validation : 88%\n\n### ✅ Actions recommandées\n1. Valider les 3 commandes en attente prioritaires\n2. Contacter TechCorp pour confirmer les prix\n3. Mettre à jour les seuils d'anomalie`,
        
        `## Rapport des commandes récentes\n\n### 🎯 Performance\n- **Taux d'extraction** : 94.2%\n- **Précision IA** : 91.8%\n- **Temps moyen de traitement** : 2.3 minutes\n\n### 📊 Répartition par statut\n\n| Statut | Nombre | Pourcentage |\n|--------|--------|-------------|\n| ✅ Validées | 67 | 75.3% |\n| ⏳ En attente | 12 | 13.5% |\n| ❌ Rejetées | 10 | 11.2% |\n\n### 🚨 Alertes actives\n1. **Commande dupliquée** détectée (Client: ACME)\n2. **Prix incohérent** sur produit REF-089\n3. **Quantité excessive** commandée par nouveau client\n\n### 💡 Optimisations suggérées\n- Ajuster les règles de détection pour les nouveaux clients\n- Améliorer la reconnaissance des formats de prix\n- Implémenter la validation automatique pour les clients de confiance`
      ]
      
      return responses[Math.floor(Math.random() * responses.length)]
    },
    
    generateAnomalyResponse(message, context) {
      return `## Détection d'anomalies\n\n### 🚨 Anomalies critiques (3)\n\n**1. Prix inhabituel détecté**\n- Produit : REF-045 Composant électronique\n- Prix commandé : 89.99 € (habituel: 45.50 €)\n- Client : TechSolutions\n- Recommandation : Vérification manuelle requise\n\n**2. Quantité suspecte**\n- Produit : REF-012 Câble USB\n- Quantité : 5,000 unités (commande habituelle: 50-200)\n- Client : Nouveau client GlobalTech\n- Recommandation : Validation commerciale\n\n**3. Client inhabituel**\n- Client : MegaCorp Industries (première commande)\n- Montant : 25,000 €\n- Recommandation : Vérification identité et solvabilité\n\n### ⚠️ Anomalies mineures (5)\n- Format d'adresse non standard (2)\n- Références produits légèrement différentes (2)\n- Délai de livraison inhabituel (1)\n\n### 📊 Analyse des tendances\n- Augmentation des anomalies de 15% cette semaine\n- Principalement liées aux nouveaux clients\n- Amélioration de la détection automatique\n\n### ✅ Actions recommandées\n1. Valider manuellement les 3 anomalies critiques\n2. Ajuster les seuils pour les nouveaux clients\n3. Mettre à jour la base de données des prix`
    },
    
    generateReportResponse(message, context) {
      return `## Rapport de performance hebdomadaire\n\n### 📈 KPIs principaux\n\n| Métrique | Cette semaine | Semaine précédente | Évolution |\n|----------|---------------|-------------------|-----------|\n| Emails traités | 1,247 | 1,156 | +7.9% 📈 |\n| Commandes extraites | 89 | 82 | +8.5% 📈 |\n| Taux de précision | 91.8% | 89.2% | +2.6% 📈 |\n| Temps moyen | 2.3 min | 2.7 min | -14.8% 📈 |\n\n### 🎯 Performance par modèle IA\n- **Llama 2** : 89.5% précision, 2.1s temps de réponse\n- **GPT-4** : 94.2% précision, 1.8s temps de réponse\n- **Code Llama** : 87.3% précision, 2.4s temps de réponse\n\n### 📊 Analyse des erreurs\n- **Faux positifs** : 4.2% (-0.8%)\n- **Faux négatifs** : 3.9% (-1.2%)\n- **Erreurs de format** : 2.1% (-0.5%)\n\n### 🔍 Insights clients\n- **Nouveaux clients** : 12 (+3)\n- **Clients actifs** : 189 (+7)\n- **Panier moyen** : 2,340 € (+12%)\n\n### 💡 Recommandations\n1. **Optimiser** les règles pour les nouveaux formats\n2. **Former** le modèle sur les erreurs récurrentes\n3. **Automatiser** davantage les validations simples\n4. **Surveiller** les performances du modèle GPT-4`
    },
    
    generateCustomerResponse(message, context) {
      return `## Analyse des clients\n\n### 👥 Vue d'ensemble\n- **Total clients** : 234\n- **Clients actifs** : 189 (80.8%)\n- **Nouveaux ce mois** : 12\n- **Clients premium** : 23\n\n### 🏆 Top clients par CA\n1. **ACME Corporation** - 45,230 € (15 commandes)\n2. **TechSolutions** - 38,750 € (22 commandes)\n3. **GlobalTrade** - 32,100 € (8 commandes)\n4. **InnovateCorp** - 28,900 € (12 commandes)\n5. **MegaSupply** - 25,600 € (18 commandes)\n\n### 📊 Segmentation\n- **Grands comptes** (>20k€) : 15 clients (35% du CA)\n- **Comptes moyens** (5-20k€) : 45 clients (40% du CA)\n- **Petits comptes** (<5k€) : 174 clients (25% du CA)\n\n### 🚨 Alertes clients\n- **TechCorp** : Baisse d'activité (-30% ce mois)\n- **NewClient Ltd** : Première grosse commande (validation requise)\n- **RegularCorp** : Changement de pattern de commande\n\n### 💡 Opportunités\n1. **Upselling** : 23 clients éligibles\n2. **Réactivation** : 15 clients inactifs depuis 3 mois\n3. **Cross-selling** : Nouveaux produits pour 34 clients`
    },
    
    generateGenericResponse(message, context) {
      const responses = [
        `Je suis là pour vous aider avec EmailAuto ! Je peux vous assister avec :\n\n🔍 **Analyse des données**\n- Emails et extraction de commandes\n- Détection d'anomalies\n- Statistiques et rapports\n\n⚙️ **Configuration**\n- Paramétrage des comptes email\n- Règles d'extraction personnalisées\n- Optimisation des modèles IA\n\n📊 **Monitoring**\n- Surveillance des performances\n- Alertes et notifications\n- Tableaux de bord\n\nQue souhaitez-vous faire exactement ?`,
        
        `Bonjour ! Je suis votre assistant IA pour EmailAuto. \n\n**État actuel du système :**\n- ✅ Tous les services sont opérationnels\n- 📧 23 emails en attente de traitement\n- 🔄 12 commandes en cours de validation\n- 🤖 Modèles IA disponibles et performants\n\n**Comment puis-je vous aider aujourd'hui ?**\n\nVous pouvez me demander d'analyser vos données, générer des rapports, ou vous aider avec la configuration du système.`
      ]
      
      return responses[Math.floor(Math.random() * responses.length)]
    },
    
    // Utilitaires
    buildFullContext(additionalContext = '') {
      const contexts = []
      
      // Contexte métier
      contexts.push(this.formattedBusinessContext)
      
      // Contexte additionnel
      if (additionalContext) {
        contexts.push(additionalContext)
      }
      
      // Contexte temporel
      const now = new Date()
      contexts.push(`Date actuelle: ${now.toLocaleDateString('fr-FR')} ${now.toLocaleTimeString('fr-FR')}`)
      
      return contexts.join('\n\n')
    },
    
    generateCacheKey(message, context, model) {
      const content = `${message}|${context}|${model}`
      return btoa(content).substring(0, 32)
    },
    
    updateStats(responseTime, tokens, success) {
      this.stats.totalMessages++
      this.stats.totalTokensUsed += tokens
      
      // Calculer le temps de réponse moyen
      if (this.stats.averageResponseTime === 0) {
        this.stats.averageResponseTime = responseTime
      } else {
        this.stats.averageResponseTime = (this.stats.averageResponseTime + responseTime) / 2
      }
      
      // Calculer le taux de succès
      const successCount = Math.floor(this.stats.totalMessages * this.stats.successRate)
      const newSuccessCount = success ? successCount + 1 : successCount
      this.stats.successRate = newSuccessCount / this.stats.totalMessages
    },
    
    // Gestion des évaluations
    async rateMessage(messageId, rating, feedback = '') {
      try {
        const evaluation = {
          id: Date.now().toString(),
          messageId,
          rating, // 'good', 'bad', 'neutral'
          feedback,
          timestamp: new Date().toISOString(),
          model: this.currentModel
        }
        
        this.evaluations.push(evaluation)
        
        // Simuler l'envoi à l'API
        await new Promise(resolve => setTimeout(resolve, 500))
        
        return { success: true }
        
      } catch (error) {
        console.error('Erreur lors de l\'évaluation:', error)
        throw error
      }
    },
    
    // Configuration
    async updateConfig(newConfig) {
      try {
        this.config = { ...this.config, ...newConfig }
        
        // Simuler la sauvegarde
        await new Promise(resolve => setTimeout(resolve, 500))
        
        return { success: true }
        
      } catch (error) {
        this.error = error.message
        throw error
      }
    },
    
    // Gestion du cache
    clearCache() {
      this.responseCache.clear()
    },
    
    // Export/Import
    async exportConversation(conversationId) {
      try {
        const conversation = this.conversations.find(c => c.id === conversationId)
        if (!conversation) {
          throw new Error('Conversation non trouvée')
        }
        
        const exportData = {
          ...conversation,
          exportedAt: new Date().toISOString(),
          version: '1.0'
        }
        
        return exportData
        
      } catch (error) {
        this.error = error.message
        throw error
      }
    },
    
    async importConversation(conversationData) {
      try {
        const conversation = {
          ...conversationData,
          id: Date.now().toString(), // Nouveau ID pour éviter les conflits
          importedAt: new Date().toISOString()
        }
        
        this.conversations.unshift(conversation)
        
        return conversation
        
      } catch (error) {
        this.error = error.message
        throw error
      }
    },
    
    // Recherche
    async searchConversations(query) {
      try {
        const results = this.conversations.filter(conversation => {
          const titleMatch = conversation.title.toLowerCase().includes(query.toLowerCase())
          const messageMatch = conversation.messages.some(message => 
            message.content.toLowerCase().includes(query.toLowerCase())
          )
          return titleMatch || messageMatch
        })
        
        return results
        
      } catch (error) {
        this.error = error.message
        throw error
      }
    },
    
    // Initialisation
    async initialize() {
      try {
        this.isLoading = true
        
        // Charger les conversations sauvegardées
        // await this.loadConversations()
        
        // Vérifier le statut des modèles
        await this.checkModelStatus(this.currentModel)
        
        // Charger le contexte métier
        await this.refreshBusinessContext()
        
        this.isConnected = true
        
      } catch (error) {
        console.error('Erreur lors de l\'initialisation:', error)
        this.error = error.message
        this.isConnected = false
      } finally {
        this.isLoading = false
      }
    },
    
    async refreshBusinessContext() {
      try {
        // Simuler le chargement du contexte depuis l'API
        await new Promise(resolve => setTimeout(resolve, 1000))
        
        // Le contexte est déjà défini dans l'état initial
        // En production, on ferait des appels API ici
        
        return this.businessContext
        
      } catch (error) {
        console.error('Erreur lors du rafraîchissement du contexte:', error)
        throw error
      }
    }
  }
})